import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, RefreshCcw, ClipboardList, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

/**
 * Bell icon with a live unread count. On tap, opens a slide-in sheet listing
 * the current user's persistent notifications.
 *
 * Audience-aware: for admin/super_admin/moderator users, this hits the
 * `/admin/notifications` endpoints (broadcast admin bell). For students, it
 * hits the student endpoints. Auto-refreshes every 60s while the tab is
 * visible.
 */
export default function NotificationsBell({ testId = "mobile-bell-btn" }) {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin", "moderator"].includes(user?.role);
  const base = isAdmin ? "/admin/notifications" : "/student/notifications";

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(base);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  }, [base]);

  useEffect(() => {
    refresh();
    const iv = setInterval(() => {
      if (!document.hidden) refresh();
    }, 60_000);
    return () => clearInterval(iv);
  }, [refresh]);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try {
        await api.post(`${base}/mark-all-read`, {});
        setUnread(0);
      } catch { /* silent */ }
    }
  };

  const dismiss = async (id) => {
    setLoading(true);
    try {
      await api.post(`${base}/${id}/dismiss`, {});
      setItems((list) => list.filter((n) => n.id !== id));
    } finally { setLoading(false); }
  };

  const takeAction = (n) => {
    setOpen(false);
    if (isAdmin) {
      // Admin bell routes to the Enrollment Requests page. We could also
      // deep-link to a specific request via ?highlight= but the top-of-list
      // is already the freshest pending item.
      if (n.type === "enrollment_pending" || n.type === "payment_resubmitted") {
        nav("/admin/enrollment-requests");
      }
      return;
    }
    // Student side: subscription_expiring deep-links to the renewal modal for
    // the exact course via ?renew=<course_id>. StudentEnrollments parses this
    // param and opens the RenewSubscriptionModal automatically.
    if (n.type === "subscription_expiring") {
      const cid = n?.data?.course_id;
      const cname = n?.data?.course_name;
      if (cid) {
        const qs = new URLSearchParams({ renew: cid });
        if (cname) qs.set("course_name", cname);
        nav(`/student/enrollments?${qs.toString()}`);
      } else {
        nav("/student/enrollments");
      }
    }
  };

  const styleForType = (t) => {
    switch (t) {
      case "subscription_expiring":
        return "border-orange-200 bg-orange-50/70 dark:bg-orange-950/20 dark:border-orange-800";
      case "enrollment_pending":
        return "border-indigo-200 bg-indigo-50/70 dark:bg-indigo-950/20 dark:border-indigo-800";
      case "payment_resubmitted":
        return "border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-800";
      default:
        return "border-slate-200 bg-white dark:bg-slate-800/40 dark:border-slate-700";
    }
  };

  const actionLabel = (t) => {
    if (t === "subscription_expiring") return { label: "Renew now", Icon: RefreshCcw };
    if (t === "enrollment_pending" || t === "payment_resubmitted") return { label: "Review request", Icon: ClipboardList };
    return null;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="relative h-10 w-10 grid place-items-center rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 transition-colors"
          data-testid={testId}
          aria-label={unread > 0 ? `You have ${unread} unread notifications` : "Notifications"}
        >
          <Bell className="w-5 h-5" strokeWidth={1.75} />
          {unread > 0 && (
            <span
              className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
              data-testid="notifications-unread-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="notifications-drawer">
        <SheetHeader className="p-4 border-b border-slate-100 dark:border-slate-800">
          <SheetTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-600" />
            {isAdmin ? "Admin Notifications" : "Notifications"}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 && (
            <div className="text-center py-16 space-y-2" data-testid="notifications-empty-state">
              <div className="mx-auto h-14 w-14 rounded-full bg-indigo-100 grid place-items-center">
                <Bell className="w-7 h-7 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div className="text-sm font-semibold text-slate-900">You're all caught up 🎉</div>
              <p className="text-xs text-slate-500 max-w-[240px] mx-auto">
                {isAdmin
                  ? "New enrollment requests and payment proofs will land here."
                  : "We'll ping you when your subscription is about to expire or when there's news to share."}
              </p>
            </div>
          )}
          {items.map((n) => {
            const action = actionLabel(n.type);
            return (
              <div
                key={n.id}
                className={`rounded-md border p-3 flex gap-3 ${styleForType(n.type)}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 dark:text-slate-50">{n.title}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                  <div className="mt-2 flex gap-2">
                    {action && (
                      <Button
                        size="sm"
                        className="h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={() => takeAction(n)}
                        data-testid={`notification-action-${n.id}`}
                      >
                        <action.Icon className="w-3 h-3 mr-1" />
                        {action.label}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-slate-500 hover:text-slate-700"
                      onClick={() => dismiss(n.id)}
                      disabled={loading}
                      data-testid={`notification-dismiss-${n.id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
